/**
 * Celah 3 (langkah 35) — SATU layar approval "barang berkurang" (ISIAN §8; K165 butir 1: tanpa ambang
 * nominal, approval KC untuk SEMUA barang berkurang).
 *
 * Dua jalur, satu layar (dijaga `tests/test_celah3_gerbang_barang_berkurang.py`):
 *   REJECT  — pengeluaran bahan pengganti ke WO (material_issue purpose REJECT_REMAKE, status SUBMITTED);
 *             penyetuju Owner / Kepala Cabang / Admin Gudang.
 *   HILANG / RUSAK — penyesuaian stok menyusut (stock_adjustment DRAFT, reason_code HILANG/RUSAK, qty < 0);
 *             penyetuju Owner / Kepala Cabang. HILANG wajib penyelidikan Admin Gudang regional sebelum disetujui
 *             (vonis Owner 19/20 Sep 2026) — backend menolak 409 PENYELIDIKAN_HILANG_WAJIB.
 * Remake tetap di layar WO (keputusan koordinator 19/20 Sep). Tombol cermin backend; penjaga = 403/409 backend.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ambilSemua } from "../../lib/ambilSemua";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { toast } from "sonner";
import { PackageMinus } from "lucide-react";
import { formatDateTime } from "../../lib/format";
import { mintaTeks } from "../../lib/dialogAplikasi";
import Hint from "../../components/etams/Hint";

const PENYETUJU_PENYESUAIAN = ["OWNER", "KEPALA_CABANG"];
const PENYETUJU_PENGELUARAN = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"];

export default function BarangBerkurang() {
  const { roleCode, profile } = useAuth();
  const [pengeluaran, setPengeluaran] = useState([]);
  const [penyesuaian, setPenyesuaian] = useState([]);
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(async () => {
    try {
      // ambilSemua: antrean persetujuan tidak boleh terpotong di halaman pertama (Kelas H).
      const [mi, adj] = await Promise.all([
        ambilSemua((p) => api.get("/inventory/material-issues/", { params: p }), { status: "SUBMITTED" }),
        ambilSemua((p) => api.get("/inventory/adjustments", { params: p }), { status: "DRAFT" }),
      ]);
      setPengeluaran(mi.filter((x) => x.purpose === "REJECT_REMAKE"));
      setPenyesuaian(adj.filter((x) => ["HILANG", "RUSAK"].includes(x.reason_code) && Number(x.qty_change) < 0));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Daftar barang berkurang gagal dimuat"));
    }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  const jalankan = async (fn, ok) => {
    setSibuk(true);
    try { await fn(); toast.success(ok); await muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal")); }
    finally { setSibuk(false); }
  };
  const tolakPengeluaran = async (x) => {
    const r = await mintaTeks("Alasan menolak/membatalkan pengeluaran bahan pengganti:", { minPanjang: 5 });
    if (r) jalankan(() => api.post(`/inventory/material-issues/${x.id}/cancel`, { reason: r }), "Pengeluaran dibatalkan.");
  };
  const tolakPenyesuaian = async (x) => {
    const r = await mintaTeks("Alasan menolak penyesuaian:", { minPanjang: 5 });
    if (r) jalankan(() => api.post(`/inventory/adjustments/${x.id}/reject`, { rejection_notes: r }), "Penyesuaian ditolak.");
  };
  const bukanSaya = (x) => x.created_by !== profile?.id && x.requested_by !== profile?.id;

  return (
    <div className="space-y-6" data-testid="barang-berkurang-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><PackageMinus className="h-6 w-6" /> Barang Berkurang — Persetujuan
          <Hint>Satu tempat untuk semua barang berkurang: bahan pengganti reject (ke WO) dan stok hilang/rusak. Remake tetap diputus di layar WO.</Hint>
        </h1>
      </div>

      <Card className="p-4 space-y-2" data-testid="bb-pengeluaran">
        <div className="font-medium">Reject — bahan pengganti ke WO ({pengeluaran.length})</div>
        {pengeluaran.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-border py-2 text-sm">
            <div>
              <Link to="/inventory/material-issues" className="font-mono text-xs underline">{x.issue_number || "—"}</Link>
              <span className="ml-2">WO <Link to={`/wo/${x.wo_id}`} className="underline">{x.wo_number || "—"}</Link></span>
              <span className="ml-2 text-muted-foreground">{formatDateTime(x.submitted_at || x.created_at)}</span>
              {x.notes && <div className="text-xs text-muted-foreground">{x.notes}</div>}
            </div>
            {PENYETUJU_PENGELUARAN.includes(roleCode) && bukanSaya(x) && (
              <div className="flex gap-2">
                <Button size="sm" disabled={sibuk} onClick={() => jalankan(() => api.post(`/inventory/material-issues/${x.id}/approve`), "Pengeluaran disetujui.")}
                        data-testid={`bb-setujui-mi-${x.id}`}>Setujui</Button>
                <Button size="sm" variant="outline" disabled={sibuk} onClick={() => tolakPengeluaran(x)}>Tolak</Button>
              </div>
            )}
          </div>
        ))}
        {pengeluaran.length === 0 && <div className="text-sm text-muted-foreground">Tidak ada yang menunggu.</div>}
      </Card>

      <Card className="p-4 space-y-2" data-testid="bb-penyesuaian">
        <div className="font-medium">Hilang / rusak — penyesuaian stok ({penyesuaian.length})</div>
        {penyesuaian.map((x) => {
          const kurangPenyelidikan = x.reason_code === "HILANG" && (x.reason || "").trim().length < 20;
          return (
            <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-border py-2 text-sm">
              <div>
                <StatusBadge tone={x.reason_code === "HILANG" ? "danger" : "warning"}>{x.reason_code === "HILANG" ? "Hilang" : "Rusak"}</StatusBadge>
                <span className="ml-2">{x.material_name || x.material_code || "—"}</span>
                <span className="ml-2 tabular-nums">{x.qty_change}</span>
                <div className="text-xs text-muted-foreground">Penyelidikan/alasan: {x.reason || "—"}</div>
                {kurangPenyelidikan && <div className="text-xs text-destructive">Penyelidikan belum cukup — Admin Gudang wilayah wajib melengkapinya sebelum disetujui.</div>}
              </div>
              {PENYETUJU_PENYESUAIAN.includes(roleCode) && bukanSaya(x) && (
                <div className="flex gap-2">
                  <Button size="sm" disabled={sibuk || kurangPenyelidikan} onClick={() => jalankan(() => api.post(`/inventory/adjustments/${x.id}/approve`, {}), "Penyesuaian disetujui.")}
                          data-testid={`bb-setujui-adj-${x.id}`}>Setujui</Button>
                  <Button size="sm" variant="outline" disabled={sibuk} onClick={() => tolakPenyesuaian(x)}>Tolak</Button>
                </div>
              )}
            </div>
          );
        })}
        {penyesuaian.length === 0 && <div className="text-sm text-muted-foreground">Tidak ada yang menunggu.</div>}
      </Card>
    </div>
  );
}
